import { FC, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { Box, Grid } from '@mui/material';
import clsx from 'clsx';

import { ReactComponent as DropdownArrow } from 'assets/icons/DropdownArrow.svg';

import { useToggle } from 'hooks/useToggler';

import {
  FieldTableRowTitle,
  FieldTableWatchedRowTitle,
} from 'components/_basic/Form/FieldTable2/components/FieldTableRowTitle';
import { NumberInputControlled } from 'components/_basic/Form/NumberInput';
import { reCalculatedPeriodValues } from 'components/BusinessToolForm/utils/helpers';

import { concatWithDot } from 'helpers/string';

import { IFieldTableRowProps } from './types';
import { MAX_SUB_ROWS, subRowInitialValues } from '../../../../utils/form';
import { FieldTableSubRow } from '../FieldTableSubRow';

import styles from './FieldTableRow.module.scss';

const defaultGetRowName = (name: string, columnName: string, rowIndex?: number) => {
  return concatWithDot(name, rowIndex?.toString() || '', columnName);
};

export const FieldTableRow: FC<IFieldTableRowProps> = ({
  name,
  rowIndex,
  columnsNames,
  disabled = false,
  rowTitle,
  watchRowTitleName = '',
  titleClass = '',
  firstColumnWidth,
  subRowName,
  getRowName = defaultGetRowName,
}) => {
  const [isNeedToRecalculate, setIsNeedToRecalculate] = useState(false);
  const { control, getValues } = useFormContext();

  const [isSubRowExpanded, setIsSubRowExpanded] = useToggle(false);

  const { update } = useFieldArray({ control, name });

  const handleSubRowToggle = () => {
    setIsSubRowExpanded(!isSubRowExpanded);
  };

  const {
    fields,
    append,
    remove,
    update: updateSubRow,
  } = useFieldArray({
    control,
    name: subRowName,
  });

  const canRemove = fields.length > 1;
  const canAddMore = fields.length < MAX_SUB_ROWS;

  const handleAddSubRow = () => {
    append(subRowInitialValues);
  };

  const handleRemoveSubRow = (index: number) => {
    remove(index);
  };

  const onBlurHandler = async (fieldName: string) => {
    if (!isNeedToRecalculate) return;

    const values = getValues(name);
    const updatedPeriodValues = reCalculatedPeriodValues(fieldName, values[rowIndex]);
    update(rowIndex, updatedPeriodValues);
    setIsNeedToRecalculate(false);
  };

  return (
    <>
      <Grid container sx={{ marginBottom: '12px' }} wrap="nowrap" gap={1}>
        <Grid
          container
          sx={{ width: firstColumnWidth, minWidth: firstColumnWidth }}
          gap={1}
          className={clsx(titleClass, styles.rowTitle)}
          onClick={handleSubRowToggle}
        >
          <Grid item className={clsx(styles.dropDownIcon, { [styles.expanded]: isSubRowExpanded })}>
            <DropdownArrow />
          </Grid>
          <Grid>
            {watchRowTitleName && (
              <FieldTableWatchedRowTitle watchRowTitleName={watchRowTitleName.replace(':index', String(rowIndex))} />
            )}
            {rowTitle && !watchRowTitleName && <FieldTableRowTitle title={rowTitle} />}
          </Grid>
        </Grid>
        <Grid item xs>
          <Grid container gap={1} columns={columnsNames.length} wrap="nowrap">
            {columnsNames.map((columnName) => {
              const fieldName = getRowName(name, columnName, rowIndex);
              return (
                <Grid key={columnName} item xs={1}>
                  <NumberInputControlled
                    control={control}
                    id={fieldName}
                    name={fieldName}
                    disabled={disabled}
                    fieldClassName={styles.tableRow}
                    onBlur={() => onBlurHandler(fieldName)}
                    onChange={() => setIsNeedToRecalculate(true)}
                    data-mirror-field={name}
                    allowNegative
                  />
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Grid>
      {isSubRowExpanded && (
        <Box className={styles.subRowsContainer}>
          {fields.map((subRowItem, index) => {
            return (
              <FieldTableSubRow
                key={subRowItem.id}
                name={subRowName}
                rowIndex={index}
                totalRowCount={fields.length}
                columnsNames={columnsNames}
                firstColumnWidth={firstColumnWidth}
                canRemove={canRemove}
                canAddMore={canAddMore}
                handleAddSubRow={handleAddSubRow}
                handleRemoveSubRow={handleRemoveSubRow}
                handleUpdateSubRow={updateSubRow}
              />
            );
          })}
        </Box>
      )}
    </>
  );
};
